import { useParams } from "react-router-dom";
import ApiStatus from "../apiStatus";
import { useDeleteStation, useFetchStation, useUpdateStation } from "../hooks/StationHooks";
import ValidationSummary from "../ValidationSummary";
import StationForm from "./StationForm";
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from "react";
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from "../AuthContext";
import { StationDTO } from "../types/stationDTO";

const StationEdit = () => {
  const { t } = useTranslation();
  const { roles } = useContext(AuthContext);
  const { organisationId, stationId } = useParams<{ organisationId: string, stationId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  if (!stationId) throw Error("Need a station id");
  const { data, status, isSuccess } = useFetchStation(organisationId, stationId);
  const updateStationMutation = useUpdateStation(organisationId);
  const deleteStationMutation = useDeleteStation(organisationId);
  const [stationToDelete, setStationToDelete] = useState<StationDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const canDelete = roles.includes("SysAdmin") || roles.includes("OptiviAdmin");

  const handleStationDelete = (station: StationDTO) => {
    setStationToDelete(station);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    if (!stationToDelete) return;

    deleteStationMutation.mutate(stationToDelete, {
      onSuccess: () => {
        setShowDeleteModal(false);
        toast.success(t('station.deleteSuccess'));
        history.back();
      },
      onError: () => {
        setShowDeleteModal(false);
        toast.error(t('station.deleteError'));
      }
    });
  };

  if (!isSuccess) return <ApiStatus status={status} />;

  const stationWithLocalDates = { 
    ...data,
    validFrom: new Date(data.validFrom + 'Z'),
    validTo: new Date(data.validTo + 'Z'),
    lastModified: new Date(data.lastModified + 'Z') 
  };

  return (
    <>
      {updateStationMutation.isError && (
        <ValidationSummary error={updateStationMutation.error} />
      )}
      {updateStationMutation.isPending ? (
        <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
          <ClipLoader size={50} color={"#123abc"} loading={true} />
          <p>{t('station.editWait')}</p>
        </div>
      ) : (
        <StationForm
          headerText={t('station.edit')}
          station={stationWithLocalDates}
          submitted={(station) => {
            updateStationMutation.mutate(station, {
              onSuccess: () => {
                toast.success(t('station.editSuccess'));
                history.back();
              },
              onError: () => {
                toast.error(t('station.editError'));
              }
            });
          }}
          deleted={canDelete ? handleStationDelete : undefined}
        />
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('station.deleteConfirmation')}</p>
          {stationToDelete && (
            <>
              <div><strong>{t('common.name')}:</strong> {stationToDelete.name}</div>
              <div><strong>{t('common.address')}:</strong> {stationToDelete.address}</div>
              <div><strong>{t('common.municipality')}:</strong> {stationToDelete.municipality}</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete} disabled={deleteStationMutation.isPending}>
            {t('buttons.delete')}
          </Button>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteStationMutation.isPending}>
            {t('buttons.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StationEdit;
